/* global paypal */
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { datadogRum } from '@datadog/browser-rum';
import useCurrency from '../../../modules/currency/hooks/use-currency';
import { useBroadcastChannel } from '../../../hooks/useBroadcastChannel';
import useCartTotalsUtils from '../../../hooks/useCartTotalsUtils';
import useApiStatus from '../../../store/apiStatus/useApiStatus';
import { defaultUrl } from '../../../commons/hooks/use-platform';
import { currentBaseUrl } from '../../../helpers/urlConst';
import useTagManager from '../../../hooks/useTagManager';
import {
  selectCart,
  selectCheckout,
  selectHasPluginSMS,
  selectAdditionalData,
  selectError,
} from '../../../store/slice';
import DataContext from '../../../store/dataContext';
import UiContext from '../../../store/uiContext';
import useBillingPhone from '../../../commons/hooks/use-billing-phone';
import useAddress from '../../../hooks/useAddress';
import { useProduct } from '../../../hooks/useProduct';
import { useEndpoint } from '../../../hooks/useEndpoint';

import SubmitBtn from '../../Button/SubmitBtn';
import BillingPhone from '../../BillingPhone';
import CartTotalsMobile from '../../../modules/cart/components/cartMobile';
import { usePickup } from '../../../modules/shipping/containers/pickup-in-store/use-pickup';
import { selectShipping } from '../../../modules/shipping/slice';

import { PaymentDescription } from '../DefaultView';

import './style.scss';

const PaypalCheckout = ({ setOrderSubmitting, flags }) => {
  const {
    billingPhone,
    broadcastMessage,
    paymentMethodData,
    paymentMethodError,
    registeredPaymentMethods,
    reset,
    setApiPostOrderError,
    setBroadcastMessage,
    setPaymentMethodError,
    setRegisteredPaymentMethods,
    storeUrl,
    moveToPlatform,
    shipCompany,
    orderSubmitting,
    additionalCharges,
  } = useContext(DataContext);
  const { updateCheckoutState, TO_DONE, setShowOverlay, allowBillingAddressAdd, setOpenPreOrder } =
    useContext(UiContext);
  const cartData = useSelector(selectCart);
  const portalSettings = cartData?.merchant?.settings;
  const { isLoading: isLoadingApiStatus } = useApiStatus();
  const { getOrderTotal } = useCartTotalsUtils();
  const { isPickupActive } = usePickup();
  const { getBilling } = useAddress();
  const { allDigital } = useProduct();
  const { postSubmit } = useEndpoint();

  const [apiPaypalExecuteLoading, setApiPaypalExecuteLoading] = useState(false);
  const [apiPaypalExecuteError, setApiPaypalExecuteError] = useState(undefined);

  const { receiveMessage } = useBroadcastChannel();
  const { dataLayer } = useTagManager();
  const [method, setMethod] = useState('paypal');
  const [payLaterZindex, setPayLaterZindex] = useState('999');
  const [localLoading, setLocalLoading] = useState(false);
  const checkoutData = useSelector(selectCheckout);
  const additionalData = useSelector(selectAdditionalData);
  const shippingState = useSelector(selectShipping);
  const submitError = useSelector(selectError);
  const hasPluginSMS = useSelector(selectHasPluginSMS);

  const isLoading = isLoadingApiStatus;
  const { t } = useTranslation();

  const { getShoppercurrency } = useCurrency();

  const { isBillingPhoneOk } = useBillingPhone();

  const buttonRef = useRef(null);
  const buttonHasRenderedRef = useRef(null);
  const isAwaiting = useRef(null);
  const paylaterContentRef = useRef(null);

  const isPhoneVisible = () =>
    ((submitError.data === 'PAYMENT DECLINED' || portalSettings.contactPhone.isEnabled) && !allowBillingAddressAdd) ||
    hasPluginSMS;

  useEffect(() => {
    if (paymentMethodData?.paypal && paymentMethodData.paypal.rel !== 'approve') {
      setPaymentMethodError({
        paypal: t('payment_method.paypal.error'),
      });
    }
  }, [paymentMethodData]);

  const postPaypal = async () => {
    const payment = {};
    payment.context = {
      cvv: undefined,
      cardType: 'alt',
      onBehalfOf: cartData?.payment?.direct?.onBehalfOf,
      paymentId: broadcastMessage.paymentId,
      payerId: broadcastMessage.payerId,
      method: method || 'paypal',
    };
    payment.id = broadcastMessage.paymentId;
    payment.type = 'paypal';
    try {
      setApiPaypalExecuteLoading(true);
      let pickUpMethodKey;
      let shippingMethodKey;
      const address = {
        ...checkoutData.address,
        name: checkoutData.address.name || additionalData.shipping.name,
        company: checkoutData.address.company || additionalData.shipping.company,
        line2: checkoutData.address.line2 || additionalData.shipping.line2,
      };

      if (isPickupActive()) {
        pickUpMethodKey = shippingState.bopis.selectedPickupMethod.lookupKey;
        shippingMethodKey = undefined;
      } else {
        pickUpMethodKey = undefined;
        shippingMethodKey = checkoutData?.shippingMethodKey;
      }
      const billing = getBilling();

      const result = await postSubmit({
        address: allDigital() ? billing : address,
        billing: allDigital()
          ? {
              phone: billingPhone,
            }
          : billing,
        shippingMethodKey,
        pickUpMethodKey,
        payment,
      });
      if (result?.checkout?.payment?.type === 'paypal') {
        dataLayer(result);
        updateCheckoutState(TO_DONE);
        reset();
        if (!result?.redirectUrl) {
          orderSubmitting(false);
        }
        datadogRum.addAction('successful-order', { 'payment-method': 'paypal' });
      }
    } catch (err) {
      setApiPaypalExecuteError(err);
    }
    setApiPaypalExecuteLoading(false);
  };

  useEffect(() => {
    if (broadcastMessage) {
      if (cartData?.items?.find(item => item?.preOrder?.expectedAvailability)) {
        setOpenPreOrder({
          payload: {},
          callback: postPaypal,
        });
      } else {
        postPaypal();
      }
    }
  }, [broadcastMessage]);

  useEffect(() => {
    if (apiPaypalExecuteLoading) {
      setOrderSubmitting(apiPaypalExecuteLoading);
    }
  }, [apiPaypalExecuteLoading]);

  useEffect(() => {
    if (apiPaypalExecuteError) {
      setApiPostOrderError(t('payment_method.paypal.error'));
      setBroadcastMessage(undefined);
      setPaymentMethodError({
        paypal: t('payment_method.paypal.error'),
      });
      orderSubmitting(false);
    }
  }, [apiPaypalExecuteError]);

  useEffect(() => {
    receiveMessage();
  }, []);

  const formatAddressLine2 = (line2, company) => {
    if (line2 && company) {
      return `${line2}, ${company}`;
    }
    if (company) {
      return company;
    }
    return line2;
  };

  const triggerClickAnimation = () => {
    const btn = paylaterContentRef.current;
    const x = paylaterContentRef.current.pageX - btn.offsetLeft;
    const y = paylaterContentRef.current.pageY - btn.offsetTop;
    const span = document.createElement('span');

    span.style.left = `${x}px`;
    span.style.top = `${y}px`;
    btn.append(span);
  };

  const createButton = () => {
    const { name, line1, line2, city, region, postal, country } = checkoutData?.address;
    const charges = additionalCharges.map(item => ({
      ...item,
      price: item.value,
      discount: 0,
    }));
    paypal.getFundingSources().forEach(fundingSource => {
      if (fundingSource === 'paylater') {
        if (buttonRef.current?.close && buttonHasRenderedRef.current) {
          buttonRef.current.close();
          buttonHasRenderedRef.current = false;
        }

        buttonRef.current = paypal.Buttons({
          fundingSource,
          createOrder: (_data, actions) =>
            actions.order.create({
              application_context: {
                shipping_preference: 'SET_PROVIDED_ADDRESS',
              },
              purchase_units: [
                {
                  amount: {
                    currency_code: getShoppercurrency()?.baseCode,
                    value: getOrderTotal(),
                    breakdown: {
                      handling: {
                        value: cartData?.totals?.handling || 0,
                        currency_code: getShoppercurrency()?.baseCode,
                      },
                      shipping: {
                        value: cartData?.totals?.shipping || 0,
                        currency_code: getShoppercurrency()?.baseCode,
                      },
                      tax_total: {
                        value: cartData?.totals?.tax !== undefined ? cartData?.totals?.tax : 0,
                        currency_code: getShoppercurrency()?.baseCode,
                      },
                      insurance: {
                        value: cartData?.totals?.insurance || 0,
                        currency_code: getShoppercurrency()?.baseCode,
                      },
                      discount: {
                        value:
                          (cartData?.totals?.discount || 0) +
                          (cartData?.totals?.coupons?.reduce((partial, a) => partial + a.amount, 0) || 0),
                        currency_code: getShoppercurrency()?.baseCode,
                      },
                      item_total: {
                        value: parseFloat(
                          String(
                            cartData?.items
                              ?.concat(charges)
                              .reduce((partial, a) => partial + a.price * a.quantity - a.discount, 0),
                          ),
                        ).toFixed(2),
                        currency_code: getShoppercurrency()?.baseCode,
                      },
                    },
                  },
                  items: cartData?.items?.concat(charges).map(item => ({
                    name: item.name,
                    quantity: item.quantity,
                    unit_amount: {
                      currency_code: getShoppercurrency()?.baseCode,
                      value: item.price - item.discount,
                    },
                  })),
                  shipping: {
                    name: {
                      full_name: name,
                    },
                    address: {
                      recipient_name: name,
                      address_line_1: line1,
                      address_line_2: formatAddressLine2(line2, shipCompany),
                      admin_area_2: city,
                      admin_area_1: region,
                      postal_code: postal,
                      country_code: country,
                    },
                  },
                },
              ],
            }),
          onInit: (_data, actions) => {
            setPayLaterZindex('1');
            if (!isBillingPhoneOk({ shouldShowError: false })) {
              actions.disable();
            }
          },
          // eslint-disable-next-line no-unused-vars
          onClick: (data, actions) => {
            isBillingPhoneOk();
            triggerClickAnimation();
          },
          onApprove: data => {
            setMethod(fundingSource);
            setBroadcastMessage({ paymentId: data.orderID, payerId: data.payerID });
          },
          onError: () => setPaymentMethodError({ paypal: t('payment_method.paypal.error') }),
          onCancel: () => setPaymentMethodError({ paypal: '' }),
        });
        if (buttonRef.current.isEligible() && document.getElementById('paypal-button-container')) {
          buttonRef.current.render('#paypal-button-container').then(() => {
            buttonHasRenderedRef.current = true;
          });
        } else {
          setRegisteredPaymentMethods({
            ...registeredPaymentMethods,
            paylater: false,
          });
        }
      }
    });
  };

  useEffect(() => {
    if (paymentMethodData?.paypal?.href) {
      if (isAwaiting.current) {
        setPayLaterZindex('999');
        const buttonList = document.getElementsByClassName('paypal-buttons-context-iframe');
        if (buttonList?.length > 1) {
          buttonList[0].parentNode.removeChild(buttonList[0]);
        }
        clearTimeout(isAwaiting.current);
      }
      isAwaiting.current = setTimeout(() => {
        createButton();
      }, [500]);
    }
    if (paymentMethodData?.paypal && paymentMethodData.paypal.rel !== 'approve') {
      setPaymentMethodError({
        paypal: t('payment_method.paypal.error'),
      });
    }
  }, [paymentMethodData, cartData, additionalCharges]);

  useEffect(() => {
    if (registeredPaymentMethods?.paylater?.isEnabled) {
      if (window.paypal) {
        if (isAwaiting.current) {
          setPayLaterZindex('999');
          const buttonList = document.getElementsByClassName('paypal-buttons-context-iframe');
          if (buttonList?.length > 1) {
            buttonList[0].parentNode.removeChild(buttonList[0]);
          }
          clearTimeout(isAwaiting.current);
        }
        isAwaiting.current = setTimeout(() => {
          createButton();
        }, [500]);
      }
    } else {
      setPayLaterZindex('1');
    }
  }, [
    registeredPaymentMethods,
    window.paypal,
    billingPhone,
    checkoutData?.line1,
    checkoutData?.line2,
    checkoutData?.city,
    checkoutData?.region,
    checkoutData?.postal,
    checkoutData?.country,
  ]);

  useEffect(() => {
    if (broadcastMessage) {
      setShowOverlay(null);
    }
  }, [broadcastMessage]);

  const handleMoveToCheckout = () => {
    if (!registeredPaymentMethods.paypal) {
      setLocalLoading(true);
      window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
    }
  };

  return (
    <div
      className={classNames({
        'paypal-space-default': true,
        'mt-unset': paymentMethodError?.paypal,
      })}
    >
      {isPhoneVisible() && <BillingPhone flags={flags} />}
      <CartTotalsMobile />
      {paymentMethodError?.paypal && <div className="paypal-error">{paymentMethodError?.paypal}</div>}
      <div className="paypal-container">
        <SubmitBtn type="paypal" style={{ layout: 'horizontal', label: 'checkout', tagline: 'false' }} />
        {moveToPlatform.paypal && !cartData?.payment?.paypal?.clientId && (
          <div
            className="move-to-platform"
            onClick={() => handleMoveToCheckout()}
            role="button"
            tabIndex="0"
            onKeyDown={null}
          />
        )}
        <div id="paypal-button-container" className={`${!registeredPaymentMethods?.paylater?.isEnabled ? 'hide' : ''}`}>
          <div className="paylater-content" style={{ zIndex: payLaterZindex }} ref={paylaterContentRef}>
            {isLoading || localLoading || payLaterZindex === '999' ? (
              <img
                className="paypal-container__loading"
                alt="Loading..."
                src={`${currentBaseUrl()}loaders/spinner-white.png`}
                width="25"
                height="25"
              />
            ) : (
              <>
                <img
                  alt="PayPal Icon"
                  className="paypal-icon"
                  // eslint-disable-next-line max-len
                  src="data:image/svg+xml;base64,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"
                />
                {t('payment_method.paylater.button')}
              </>
            )}
          </div>
        </div>
      </div>
      {registeredPaymentMethods?.paylater?.isEnabled ? (
        <div className="paypal-info">
          <div className="paypal-labels">
            <p className="paypal-paylater">{t('paypal_checkout.defaultAndPaylater')}</p>
          </div>
          <p className="paypal-description">{t('paypal_checkout.description')}</p>
        </div>
      ) : (
        <PaymentDescription />
      )}
    </div>
  );
};

export default PaypalCheckout;

PaypalCheckout.propTypes = {
  setOrderSubmitting: PropTypes.func.isRequired,
  flags: PropTypes.shape({
    'disable-ddi-phone': PropTypes.bool,
  }),
};
